import type { KpiDef, Project, PulseProject, Task } from "@playbookai/types";

/**
 * Mock seed data for the SOLVE mock client's domain reads (KPI library,
 * projects, tasks). This is the seed layer — kept out of
 * `@playbookai/widget-schemas`, which stays validation-only (ADR-0012). Ported
 * from the pbdesign behaviour oracle (`src/ui/widgets/kpiData.ts`,
 * `wireframe/data/projects.ts`, the widget bodies). The live `/v1/...` swap
 * replaces the mock client, not these shapes. Personal Home no longer reads
 * any of this: it resolves live datasets through the dashboard capability
 * (plan §13.1).
 */

// --- KPI library -----------------------------------------------------------

export const KPI_LIBRARY: KpiDef[] = [
  {
    id: "door-fpy",
    name: "Door Sub-Line FPY",
    value: "91.4%",
    numeric: 91.4,
    unit: "%",
    status: "critical",
    delta: "−2.1pp",
    deltaNumeric: -2.1,
    series: [94.1, 93.8, 93.5, 93.2, 92.6, 92.1, 91.4],
    threshold: "Target ≥ 95% · Red < 93%",
    commentary:
      "Three of the last four shifts red — adhesive bead is the suspect; get the door cell on it this morning.",
  },
  {
    id: "robot-14",
    name: "Robot 14 Cycle",
    value: "47.2s",
    numeric: 47.2,
    unit: "s",
    status: "watch",
    delta: "+1.8s",
    deltaNumeric: 1.8,
    inverse: true,
    series: [45.4, 45.6, 45.9, 46.2, 46.6, 46.9, 47.2],
    threshold: "Target ≤ 45.4s · Watch > 46.0s",
    commentary:
      "Same fingerprint as the FY24 Q2 torch-tip event. Schedule the change before it picks our schedule for us.",
  },
  {
    id: "paint-oee",
    name: "Paint Booth OEE",
    value: "76.4%",
    numeric: 76.4,
    unit: "%",
    status: "watch",
    delta: "−1.4pp",
    deltaNumeric: -1.4,
    series: [78.2, 78.0, 77.8, 77.5, 77.0, 76.7, 76.4],
    threshold: "Target ≥ 80% · Watch < 78%",
    commentary:
      "Availability is solid — the loss is performance on second shift. Put the shift lead on it tonight.",
  },
  {
    id: "inbound-dpmo",
    name: "Inbound DPMO",
    value: "412",
    numeric: 412,
    status: "healthy",
    delta: "−18",
    deltaNumeric: -18,
    inverse: true,
    series: [468, 455, 447, 438, 430, 421, 412],
    threshold: "Target ≤ 500 · Watch > 600",
    commentary:
      "Right direction, three weeks running. Tier 6 scorecards are doing the work — keep up the supplier reviews.",
  },
  {
    id: "final-takt",
    name: "Final Assy Takt",
    value: "58s",
    numeric: 58,
    unit: "s",
    status: "healthy",
    delta: "0",
    deltaNumeric: 0,
    inverse: true,
    series: [58, 58, 58, 58, 58, 58, 58],
    threshold: "Target = 58s",
    commentary:
      "Right on the number. Final assy is our anchor today — protect it while we work BIW.",
  },
  {
    id: "weld-energy",
    name: "Welding Energy/Unit",
    value: "3.4kWh",
    numeric: 3.4,
    unit: "kWh",
    status: "watch",
    delta: "+0.2",
    deltaNumeric: 0.2,
    inverse: true,
    series: [3.1, 3.2, 3.2, 3.3, 3.3, 3.4, 3.4],
    threshold: "Target ≤ 3.2kWh",
    commentary:
      "Robot 14 owns ~60% of this week's rise. Fix the robot, fix the bill — same root cause.",
  },
  {
    id: "scrap-pct",
    name: "Scrap %",
    value: "1.8%",
    numeric: 1.8,
    unit: "%",
    status: "watch",
    delta: "+0.3pp",
    deltaNumeric: 0.3,
    inverse: true,
    series: [1.4, 1.4, 1.5, 1.6, 1.6, 1.7, 1.8],
    threshold: "Target ≤ 1.2% · Watch > 1.5%",
    commentary:
      "Line 3 again — same sub-line as the FPY miss. One fix should move both numbers.",
  },
  {
    id: "ot-hours",
    name: "Overtime Hours",
    value: "182h",
    numeric: 182,
    unit: "h",
    status: "healthy",
    delta: "−12h",
    deltaNumeric: -12,
    inverse: true,
    series: [212, 208, 200, 196, 190, 186, 182],
    threshold: "Target ≤ 200h/wk",
    commentary:
      "Back inside the guardrail. Hold the line — don't let BIW issues pull us into overtime.",
  },
];

export const KPI_BY_ID: Record<string, KpiDef> = Object.fromEntries(
  KPI_LIBRARY.map((k) => [k.id, k]),
);

// --- Projects (representative subset of the oracle Cockpit fixtures) -------

export const SEED_PROJECTS: Project[] = [
  {
    id: "p001",
    name: "Door Cell FPY Recovery",
    status: "Active",
    health: "red",
    objective:
      "Restore Door Sub-Line FPY from 91.4% to 95%+ and sustain for 10 shifts.",
    valueStream: "Body-in-White Door Sub-Line",
    linkedKPI: "Door Sub-Line FPY",
    currentKPI: "91.4%",
    targetKPI: "95%+",
    beforeKPI: "94.1%",
    owner: "Maria Gomez",
    sponsor: "VP Manufacturing",
    team: ["J. Chen", "Aisha Khan"],
    type: "Recovery Project",
    source: "DPM Red KPI",
    hoshinGoal: "Quality First / FPY Improvement",
    targetDate: "2026-05-12",
    openSolves: 2,
    openActions: 5,
    openRisks: 3,
    nextMilestone: "Adhesive bead check before 09:00",
    aiSignal: "Similar pattern to FY24 Q2 torch-tip event found.",
    summary:
      "Created from a red DPM signal. Door Sub-Line FPY has been below target for 3 shifts; working hypothesis is adhesive bead variation and possible Robot 14 torch-tip degradation.",
    businessImpact:
      "Reduce rework, stabilize delivery, and improve first-pass yield.",
    problemStatement: "Door Sub-Line FPY has stayed below target for 3 shifts.",
    expectedOutcome: "FPY restored to 95%+ and sustained for 10 shifts.",
    sustainRequirement: "10 shifts above 95%",
    actions: [
      {
        id: "a1",
        title: "Confirm adhesive bead variation before 09:00",
        owner: "Maria",
        due: "Today 09:00",
        status: "In Progress",
      },
      {
        id: "a2",
        title: "Review Robot 14 torch-tip condition",
        owner: "J. Chen",
        due: "Today",
        status: "Open",
      },
    ],
    milestones: [
      {
        id: "m1",
        title: "Confirm root cause hypothesis",
        status: "In Progress",
      },
      {
        id: "m2",
        title: "Run controlled countermeasure trial",
        status: "Planned",
      },
    ],
    risks: [
      {
        id: "r1",
        title: "Supplier material lot variation not confirmed",
        severity: "high",
        owner: "Priya",
        mitigation: "Pull lot certificates from last 5 deliveries.",
        escalate: true,
      },
    ],
    linkedSolves: ["Adhesive bead defect investigation"],
    kaizens: ["Standardize adhesive bead inspection"],
    decisions: [
      {
        id: "d1",
        title: "Approved maintenance window for Robot 14 inspection",
        date: "Apr 24",
      },
    ],
    yokotenLearnings: ["Torch-tip condition check before high-volume shift"],
    activity: [
      { id: "v1", when: "2h ago", text: "Maria added a containment action." },
    ],
  },
  {
    id: "p002",
    name: "Robot 14 Cycle Time Reduction",
    status: "At Risk",
    health: "amber",
    objective:
      "Reduce Robot 14 cycle time from 47.2s to 45.4s without quality loss.",
    valueStream: "Body-in-White Main Line",
    linkedKPI: "Robot 14 Cycle Time",
    currentKPI: "47.2s",
    targetKPI: "45.4s",
    beforeKPI: "45.4s",
    owner: "J. Chen",
    sponsor: "Plant Operations Lead",
    team: ["Dan K.", "Maria Gomez"],
    type: "Cross-functional Project",
    source: "VSM Kaizen Burst",
    hoshinGoal: "Flow Stability",
    targetDate: "2026-05-06",
    openSolves: 1,
    openActions: 4,
    openRisks: 2,
    nextMilestone: "Maintenance window confirmation",
    aiSignal: "Milestone at risk because the change window is not locked.",
    summary:
      "Cycle time on Robot 14 is the bottleneck for the main line; a kaizen burst identified torch-path optimization as the primary lever.",
    businessImpact:
      "Recover ~1.8s of cycle time per part, unlocking ~3% throughput.",
    problemStatement:
      "Robot 14 cycle time drifted from 45.4s baseline to 47.2s.",
    expectedOutcome:
      "Stabilize cycle time at or below 45.4s with no FPY regression.",
    actions: [
      {
        id: "a1",
        title: "Confirm maintenance change window",
        owner: "J. Chen",
        due: "Apr 28",
        status: "In Progress",
      },
    ],
    milestones: [
      { id: "m1", title: "Lock maintenance window", status: "In Progress" },
    ],
    risks: [
      {
        id: "r1",
        title: "Maintenance window not yet locked",
        severity: "high",
        owner: "J. Chen",
        mitigation: "Escalate to plant ops lead.",
        escalate: true,
      },
    ],
    linkedSolves: ["Robot 14 OEE recovery"],
    kaizens: ["Torch path optimization"],
    decisions: [
      {
        id: "d1",
        title: "Approved kaizen burst from VSM review",
        date: "Apr 18",
      },
    ],
    yokotenLearnings: ["Motion-program review before any cycle-time tuning"],
    activity: [
      { id: "v1", when: "Yesterday", text: "Dan K. attached path trace." },
    ],
  },
  {
    id: "p003",
    name: "Inbound Quality Stabilization",
    status: "Active",
    health: "green",
    objective:
      "Reduce inbound DPMO from 412 to below 250 via supplier quality stabilization.",
    valueStream: "Inbound Material Flow",
    linkedKPI: "Inbound DPMO",
    currentKPI: "412",
    targetKPI: "<250",
    beforeKPI: "474",
    owner: "Aisha Khan",
    sponsor: "Head of Supplier Quality",
    team: ["Priya Patel"],
    type: "Standardization Project",
    source: "Supplier Quality Review",
    hoshinGoal: "Supplier Quality Stabilization",
    targetDate: "2026-05-20",
    openSolves: 1,
    openActions: 3,
    openRisks: 1,
    nextMilestone: "Supplier review follow-up",
    aiSignal: "Trend improving; keep the supplier review cadence.",
    summary:
      "Inbound DPMO has steadily come down since adopting weekly supplier reviews and a tighter receiving inspection plan.",
    businessImpact:
      "Reduces line-side rework and avoids ~$120k/quarter of containment cost.",
    problemStatement:
      "Inbound DPMO peaked at 474 in March, above the 250 target.",
    expectedOutcome:
      "Sustained DPMO below 250 with monthly supplier scorecard review.",
    actions: [
      {
        id: "a1",
        title: "Send March scorecard to top 3 suppliers",
        owner: "Aisha",
        due: "Apr 28",
        status: "In Progress",
      },
    ],
    milestones: [
      { id: "m1", title: "Roll out updated checklist", status: "In Progress" },
    ],
    risks: [
      {
        id: "r1",
        title: "Supplier B reverting to old packaging spec",
        severity: "medium",
        owner: "Aisha",
        mitigation: "Confirm spec compliance in next shipment.",
        escalate: false,
      },
    ],
    linkedSolves: ["Inbound DPMO root-cause analysis"],
    kaizens: ["Weekly supplier scorecard"],
    decisions: [
      {
        id: "d1",
        title: "Approved monthly supplier scorecard cadence",
        date: "Apr 10",
      },
    ],
    yokotenLearnings: ["Weekly supplier review cadence"],
    activity: [
      { id: "v1", when: "3d ago", text: "DPMO trend confirmed improving." },
    ],
  },
];

/** Derive the `projectPulse` projection from the seed projects. */
export const PULSE_PROJECTS: PulseProject[] = SEED_PROJECTS.map((p) => ({
  id: p.id,
  name: p.name,
  health: p.health,
  kpiLine: `${p.linkedKPI} ${p.currentKPI} → Target ${p.targetKPI}`,
  pulse: `Next: ${p.nextMilestone}`,
}));

// --- Tasks (myTasks personal inbox) ----------------------------------------

export const SEED_TASKS: Task[] = [
  {
    id: "t1",
    title: "Approve containment plan for SLV-2391",
    due: "Today",
    urgent: true,
    why: "Without containment signed today, tomorrow's shipment is at risk — make the call so the floor can move.",
    bucket: "today",
  },
  {
    id: "t2",
    title: "Review OEE drop on Robot 14",
    due: "Today",
    urgent: true,
    why: "We've seen this drift before and it cost us downtime — get in front of it now while it's still cheap.",
    bucket: "today",
  },
  {
    id: "t3",
    title: "Prep for Weekly Transformation Review",
    due: "Apr 23",
    urgent: false,
    why: "Walking in without the latest containment is how we lose the room — prep it tight.",
    bucket: "today",
  },
  {
    id: "t4",
    title: "Sign off on Hoshin Q1 catchball",
    due: "Tomorrow",
    urgent: false,
    why: "Five of six objectives are committed — your sign-off is the only thing between teams and execution.",
    bucket: "later",
  },
  {
    id: "t5",
    title: "Coach: J. Chen on A3 Define quality",
    due: "Apr 24",
    urgent: false,
    why: "Six days on Define is too long — 15 minutes of coaching now saves a week of rework downstream.",
    bucket: "later",
  },
];
